import { useCallback, useMemo, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface CodeListInputProps {
  /** La liste telle qu'elle est stockee : un tableau de codes. */
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
  id?: string;
  /** Le nom lisible d'un code, ou `undefined` si le referentiel l'ignore. */
  labelFor: (code: string) => string | undefined;
  /** Le selecteur qui ajoute un code. Il ne retient jamais de valeur. */
  picker: (add: (code: string) => void) => ReactNode;
}

/**
 * Relit une liste venue d'ailleurs sans rien perdre ni rien repeter.
 *
 * La colonne est un tableau Postgres, mais les lignes ecrites a la main avant
 * le selecteur peuvent porter un code en minuscules ou des espaces.
 */
export function parseCodeList(value: string[] | undefined): string[] {
  if (!value) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of value) {
    const code = part.trim().toUpperCase();
    if (!code || seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
}

/**
 * Saisie d'une liste de codes par choix, et non par frappe (#228, #229).
 *
 * La valeur est le tableau que la colonne stocke : ce composant change la
 * facon de le composer, pas ce qui est stocke. Un code deja choisi n'est pas
 * ajoute deux fois, et une liste venue d'ailleurs est relue telle quelle — y
 * compris un code que le referentiel ne connait pas, affiche brut plutot
 * qu'efface en silence, parce qu'une valeur tapee avant le selecteur doit
 * rester visible pour etre corrigee.
 *
 * Le selecteur est fourni par l'appelant : celui des pays et celui des devises
 * n'ont ni la meme apparence ni le meme referentiel, seule la liste autour se
 * comporte pareil.
 */
export function CodeListInput({
  value,
  onChange,
  disabled,
  id,
  labelFor,
  picker,
}: CodeListInputProps) {
  const codes = useMemo(() => parseCodeList(value), [value]);

  const add = useCallback(
    (code: string) => {
      if (!code || codes.includes(code)) return;
      onChange?.([...codes, code]);
    },
    [codes, onChange],
  );

  const remove = useCallback(
    (code: string) => {
      onChange?.(codes.filter((c) => c !== code));
    },
    [codes, onChange],
  );

  return (
    <div>
      {codes.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-1.5" data-testid={id ? `${id}-chips` : undefined}>
          {codes.map((code) => {
            const label = labelFor(code) ?? code;
            return (
              <li
                key={code}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border border-[#e0e0e0]',
                  'bg-[#fafafa] py-0.5 pl-2.5 pr-1 text-[12px] text-[#1a1a1a]',
                  disabled && 'opacity-60',
                )}
              >
                {label}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => remove(code)}
                    aria-label={`Remove ${label}`}
                    className="flex h-4 w-4 items-center justify-center rounded-full text-[#999] hover:bg-[#eee] hover:text-[#1a1a1a]"
                  >
                    ×
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {picker(add)}
    </div>
  );
}
